import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import PostalMime from "postal-mime";

const containerStyle = {
  width: "100%",
  height: "100%",
  overflow: "auto",
  backgroundColor: "#f3f3f3",
  padding: "24px 0",
  boxSizing: "border-box",
};

// Word-like page styling
const docxPageStyle = {
  width: "8.5in",
  maxWidth: "100%",
  minHeight: "11in",
  margin: "0 auto 24px",
  padding: "1in",
  backgroundColor: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
  fontFamily: "'Calibri', 'Segoe UI', Arial, sans-serif",
  fontSize: "11pt",
  lineHeight: "1.5",
  color: "#000",
  boxSizing: "border-box",
};

// Plain container for non-docx (xlsx, eml)
const plainContainerStyle = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "30px",
  backgroundColor: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  fontFamily: "Arial, sans-serif",
  fontSize: "14px",
  lineHeight: "1.6",
  color: "#333",
  boxSizing: "border-box",
};

const loadingStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  color: "#666",
  fontSize: "16px",
};

const docxStyles = `
  .docx-content h1, .docx-content h2, .docx-content h3, .docx-content h4, .docx-content h5, .docx-content h6 {
    font-family: 'Calibri Light', 'Segoe UI', Arial, sans-serif;
    color: #2e74b5;
    font-weight: normal;
    margin: 12pt 0 6pt;
    line-height: 1.3;
  }
  .docx-content h1 { font-size: 28pt; color: #2e74b5; border-bottom: 1px solid #2e74b5; padding-bottom: 4pt; }
  .docx-content h2 { font-size: 18pt; color: #2e74b5; }
  .docx-content h3 { font-size: 14pt; color: #1f4d78; }
  .docx-content h4 { font-size: 12pt; font-style: italic; color: #2e74b5; }
  .docx-content h5 { font-size: 11pt; color: #2e74b5; }
  .docx-content h6 { font-size: 11pt; font-style: italic; color: #1f4d78; }
  .docx-content p { margin: 0 0 8pt 0; text-align: justify; }
  .docx-content a { color: #0563c1; text-decoration: underline; }
  .docx-content strong, .docx-content b { font-weight: bold; }
  .docx-content em, .docx-content i { font-style: italic; }
  .docx-content u { text-decoration: underline; }
  .docx-content ul, .docx-content ol { margin: 0 0 8pt 0; padding-left: 36pt; }
  .docx-content li { margin: 4pt 0; }
  .docx-content table {
    border-collapse: collapse;
    margin: 8pt 0;
    width: 100%;
    font-size: 11pt;
  }
  .docx-content table td, .docx-content table th {
    border: 1px solid #000;
    padding: 4pt 8pt;
    vertical-align: top;
  }
  .docx-content table th { background-color: #4472c4; color: #fff; font-weight: bold; }
  .docx-content blockquote {
    border-left: 3pt solid #2e74b5;
    margin: 8pt 0 8pt 12pt;
    padding-left: 12pt;
    font-style: italic;
    color: #595959;
  }
  .docx-content img { max-width: 100%; height: auto; margin: 8pt 0; }
  .docx-content pre {
    font-family: 'Consolas', 'Courier New', monospace;
    background: #f5f5f5;
    padding: 8pt;
    border: 1px solid #ddd;
    border-radius: 2px;
    white-space: pre-wrap;
    font-size: 10pt;
  }
  .docx-content code {
    font-family: 'Consolas', 'Courier New', monospace;
    background: #f5f5f5;
    padding: 1pt 4pt;
    border-radius: 2px;
    font-size: 10pt;
  }
  /* Common tables/email styles */
  .plain-content table { border-collapse: collapse; margin-bottom: 20px; }
  .plain-content th, .plain-content td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  .plain-content th { background-color: #f5f5f5; font-weight: bold; }
  .sheet-name { font-weight: bold; font-size: 16px; margin: 20px 0 10px; color: #1bace4; }
  .email-header { background: #f5f5f5; padding: 15px; border-radius: 4px; margin-bottom: 20px; }
  .email-header div { margin: 5px 0; }
  .email-header strong { display: inline-block; min-width: 70px; color: #555; }
  .email-attachments { margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; }
  .email-attachments h4 { margin-bottom: 10px; }
  .email-attachments ul { margin: 0; padding-left: 20px; }
`;

const OfficePreview = ({ url, fileExtension, onError }) => {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      try {
        setLoading(true);
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;

        let renderedHtml = "";

        if (fileExtension === "docx") {
          const result = await mammoth.convertToHtml(
            { arrayBuffer },
            {
              styleMap: [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
                "p[style-name='Heading 4'] => h4:fresh",
                "p[style-name='Heading 5'] => h5:fresh",
                "p[style-name='Heading 6'] => h6:fresh",
                "p[style-name='Title'] => h1.title:fresh",
                "p[style-name='Subtitle'] => h2.subtitle:fresh",
                "p[style-name='Quote'] => blockquote:fresh",
                "p[style-name='Intense Quote'] => blockquote.intense:fresh",
                "r[style-name='Strong'] => strong",
                "r[style-name='Emphasis'] => em",
                "r[style-name='Intense Emphasis'] => em.intense",
              ],
              convertImage: mammoth.images.imgElement((image) =>
                image.read("base64").then((imageBuffer) => ({
                  src: `data:${image.contentType};base64,${imageBuffer}`,
                }))
              ),
            }
          );
          renderedHtml = result.value;
        } else if (fileExtension === "doc") {
          throw new Error("Legacy .doc format is not supported. Please convert to .docx or download the file.");
        } else if (["xlsx", "xls"].includes(fileExtension)) {
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          renderedHtml = workbook.SheetNames.map((sheetName) => {
            const sheet = workbook.Sheets[sheetName];
            const sheetHtml = XLSX.utils.sheet_to_html(sheet, { header: "" });
            return `<div class="sheet-name">${sheetName}</div>${sheetHtml}`;
          }).join("");
        } else if (fileExtension === "eml") {
          const parser = new PostalMime();
          const email = await parser.parse(arrayBuffer);

          const header = `
            <div class="email-header">
              ${email.from ? `<div><strong>From:</strong> ${escapeHtml(email.from.name || "")} &lt;${escapeHtml(email.from.address || "")}&gt;</div>` : ""}
              ${email.to && email.to.length ? `<div><strong>To:</strong> ${email.to.map((t) => `${escapeHtml(t.name || "")} &lt;${escapeHtml(t.address || "")}&gt;`).join(", ")}</div>` : ""}
              ${email.cc && email.cc.length ? `<div><strong>Cc:</strong> ${email.cc.map((t) => `${escapeHtml(t.name || "")} &lt;${escapeHtml(t.address || "")}&gt;`).join(", ")}</div>` : ""}
              ${email.subject ? `<div><strong>Subject:</strong> ${escapeHtml(email.subject)}</div>` : ""}
              ${email.date ? `<div><strong>Date:</strong> ${new Date(email.date).toLocaleString()}</div>` : ""}
            </div>
          `;
          const body = email.html || (email.text ? `<pre style="white-space: pre-wrap; font-family: inherit;">${escapeHtml(email.text)}</pre>` : "<p><em>No content</em></p>");
          const attachments = email.attachments && email.attachments.length
            ? `<div class="email-attachments">
                <h4>Attachments (${email.attachments.length})</h4>
                <ul>${email.attachments.map((a) => `<li>${escapeHtml(a.filename || "attachment")} (${formatBytes(a.content ? a.content.byteLength : 0)})</li>`).join("")}</ul>
              </div>`
            : "";
          renderedHtml = header + body + attachments;
        } else {
          throw new Error(`Preview not supported for .${fileExtension} files.`);
        }

        if (!cancelled) {
          setHtml(renderedHtml);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          onError && onError(err.message);
        }
      }
    };

    loadDocument();
    return () => {
      cancelled = true;
    };
  }, [url, fileExtension, onError]);

  if (loading) {
    return <div style={loadingStyle}>Loading preview...</div>;
  }

  const isDocx = fileExtension === "docx";

  return (
    <div style={containerStyle}>
      <style>{docxStyles}</style>
      <div
        style={isDocx ? docxPageStyle : plainContainerStyle}
        className={isDocx ? "docx-content" : "plain-content"}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
};

const escapeHtml = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

OfficePreview.propTypes = {
  url: PropTypes.string.isRequired,
  fileExtension: PropTypes.string.isRequired,
  onError: PropTypes.func,
};

export default OfficePreview;
